import React, { useState } from 'react';
import { useFormik } from 'formik';
import { PublicationOptionFormView } from './PublicationOptionFormView';
import { useReturn } from 'hooks';
import { ROUTES } from 'routes';
import { ClassifiedsService, OffersService, Tracking } from 'api';
import { useDeposit } from 'providers';
import { Loader } from 'components';
import { PublicationOption, Strength, User } from 'types';
import { useHistory } from 'react-router-dom';
import { InvoicesService } from 'api/Invoices/InvoicesService';
import { useMountEffect } from 'hooks/use-mount-effect';
import * as Sentry from '@sentry/react';
import { PayboxService } from 'api/Paybox/PayboxService';

interface Props {
  user: User;
}

export const PublicationOptionForm: React.FC<Props> = ({ user }) => {
  const deposit = useDeposit();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const [publicationOptions, setPublicationOptions] = useState<PublicationOption[]>([]);
  const [strengths, setStrengths] = useState<Strength[]>([]);

  useMountEffect(() => {
    const logAndSetPublicationOptions = (po: PublicationOption[]) => {
      if (po.length <= 2) {
        Sentry.captureMessage(`${po.length} offer(s) shown, ${JSON.stringify(po)}`);
      }
      setPublicationOptions(po);
    };

    ClassifiedsService.getStrengths(deposit)
      .then((r: Strength[]) => {
        setStrengths(r);
        OffersService.getOffers(deposit.price!, r.length)
          .then(logAndSetPublicationOptions)
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        OffersService.getOffers(deposit.price!, 0)
          .then(logAndSetPublicationOptions)
          .finally(() => {
            setLoading(false);
          });
      });
  });

  const { values, handleChange, handleSubmit, setFieldValue, errors, isSubmitting } = useFormik({
    initialValues: {
      productIds: ['PO_SECURITEL'],
      cgu: false,
      totalPrice: 0,
      pbxTypeCarte: undefined,
    },
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      if (!values.cgu) {
        setFieldError('cgu', 'Veuillez acceptez les informations légales.');
        Tracking.sendCurrentPageError();
        setSubmitting(false);
        return;
      }

      if (values.totalPrice && !values.pbxTypeCarte) {
        setFieldError('pbxTypeCarte', 'Veuillez choisir une option de paiement.');
        Tracking.sendCurrentPageError();
        setSubmitting(false);
        return;
      }
      setSubmitting(true);
      await new Promise<void>(async (resolve, reject) => {
        try {
          const selectedPublicationOptions = publicationOptions.reduce<string[]>((selectedOptions, currentValue) => {
            if (values.productIds.includes(currentValue.id)) {
              return selectedOptions.concat(currentValue.publicationOptions);
            }
            return selectedOptions;
          }, []);
          const classified = await deposit.publishClassified(selectedPublicationOptions);
          if (classified) {
            try {
              if (values.totalPrice && values.pbxTypeCarte) {
                const invoice = await InvoicesService.createTransaction(classified.reference, user, values.productIds);
                if (invoice) {
                  history.push(ROUTES.payment, {
                    transactionId: invoice.id,
                    username: invoice.username,
                    price: invoice.price,
                    typeCarte: values.pbxTypeCarte,
                    vehicleCategory: classified.vehicle.category,
                    action: 'create',
                  });
                }
              }
            } catch (_) {
              // Temporary redirect to old deposit
              window.location.href = PayboxService.buildPbxEffectueURL(0, classified.vehicle.category, 'create');
            }
          }
          // Temporary redirect to old deposit
          window.location.href = PayboxService.buildPbxEffectueURL(0, classified.vehicle.category, 'create');
        } catch (e) {
          const message = (e as Error)?.message || '';
          setErrorMessage(message);
          Tracking.sendCurrentPageError();
          setSubmitting(false);
          reject();
        }
      });
    },
  });

  const onReturn = useReturn(ROUTES.vehiclePrice);

  return loading ? (
    <Loader />
  ) : (
    <PublicationOptionFormView
      publicationOptions={publicationOptions}
      strengths={strengths}
      phone={deposit.contacts?.phone1?.value}
      values={values}
      isSubmitting={isSubmitting}
      errorMessage={errorMessage}
      errors={errors}
      setFieldValue={setFieldValue}
      onSubmit={handleSubmit}
      onChange={handleChange}
      onReturn={onReturn}
    />
  );
};
